import * as t from 'io-ts';

import {ApiType} from '../../../models/api/apiTypes';
import {CompanyModel} from '../../../models/api/companyModel';
import {fetchApiAsync} from '../../../services/http/http';
import {ResourceListPagination, ResourceTableCommonPageType} from './resourceListPaginationHelpers';

/*
 * Consts.
 */

const DEFAULT_IDS_PAGE_SIZE = 50;

/*
 * Types.
 */

interface ResourceListIdsPageType<TResource> extends ResourceTableCommonPageType<TResource> {
  nextOffset: number;
}

interface ResourceListPaginationIdsConfig<TResource> {
  pageSize?: number;
  makeUrl: (ids: ReadonlyArray<number>, company: CompanyModel) => string;
  apiType: ApiType<t.Type<ReadonlyArray<TResource>, unknown>>;
  ids: ReadonlyArray<number>;
}

/**
 * Abstracts the pagination fetch for a list of ids.
 * Example: tagsIds = [1, 2, ..., 200] -> fetch tags in a
 * paginated way.
 *
 * @param config The config of the pagination.
 */
export function makeResourceListPaginationIds<TResource>(
  config: ResourceListPaginationIdsConfig<TResource>,
): ResourceListPagination<TResource, number, ResourceListIdsPageType<TResource>> {
  const {apiType, makeUrl, ids, pageSize = DEFAULT_IDS_PAGE_SIZE} = config;

  return {
    fetchApi: async (maybePageParam, company, cancelToken) => {
      const pageParam = maybePageParam ?? 0;
      const nextOffset = pageParam + pageSize;

      const requestedIds = ids.filter((id, index) => index >= pageParam && index < nextOffset);

      if (requestedIds.length === 0) {
        return {items: [], nextOffset};
      }

      const url = makeUrl(requestedIds, company);

      const items = await fetchApiAsync(apiType, url, {cancelToken});

      return {items, nextOffset};
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextOffset > ids.length) {
        return undefined;
      }

      return lastPage.nextOffset;
    },
  };
}
